import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Package, Plus, Edit, Trash2, AlertTriangle, Bell } from 'lucide-react';
import { stockAPI } from '@/lib/api';

const StocksIndent = () => {
  const [stocks, setStocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [newStock, setNewStock] = useState({
    item_name: '',
    category: 'Gold',
    quantity: '',
    unit: 'grams',
    reorder_point: '',
    supplier: '',
    location: '',
    reminder_date: '',
    reminder_notes: ''
  });

  useEffect(() => {
    loadStocks();
  }, []);

  const loadStocks = async () => {
    setIsLoading(true);
    try {
      const data = await stockAPI.getAll();
      setStocks(data);
    } catch (error) {
      toast.error('Failed to load stocks');
      console.error('Error loading stocks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStock = async () => {
    if (!newStock.item_name || !newStock.quantity) {
      toast.error('Please fill in item name and quantity');
      return;
    }

    try {
      const created = await stockAPI.create({
        ...newStock,
        quantity: parseFloat(newStock.quantity),
        reorder_point: newStock.reorder_point ? parseFloat(newStock.reorder_point) : null
      });
      setStocks([...stocks, created]);
      setNewStock({
        item_name: '',
        category: 'Gold',
        quantity: '',
        unit: 'grams',
        reorder_point: '',
        supplier: '',
        location: '',
        reminder_date: '',
        reminder_notes: ''
      });
      setIsAddDialogOpen(false);
      toast.success('Stock item added successfully');
    } catch (error) {
      toast.error('Failed to add stock item');
      console.error('Error adding stock:', error);
    }
  };

  const handleEditStock = async () => {
    if (!editingStock.item_name || !editingStock.quantity) {
      toast.error('Please fill in item name and quantity');
      return;
    }

    try {
      const updated = await stockAPI.update(editingStock.id, {
        item_name: editingStock.item_name,
        category: editingStock.category,
        quantity: parseFloat(editingStock.quantity),
        unit: editingStock.unit,
        reorder_point: editingStock.reorder_point ? parseFloat(editingStock.reorder_point) : null,
        supplier: editingStock.supplier,
        location: editingStock.location,
        reminder_date: editingStock.reminder_date || null,
        reminder_notes: editingStock.reminder_notes || null
      });
      setStocks(stocks.map(s => s.id === editingStock.id ? updated : s));
      setIsEditDialogOpen(false);
      setEditingStock(null);
      toast.success('Stock item updated successfully');
    } catch (error) {
      toast.error('Failed to update stock item');
      console.error('Error updating stock:', error);
    }
  };

  const handleDeleteStock = async (id) => {
    if (!window.confirm('Are you sure you want to delete this stock item?')) return;

    try {
      await stockAPI.delete(id);
      setStocks(stocks.filter(s => s.id !== id));
      toast.success('Stock item deleted successfully');
    } catch (error) {
      toast.error('Failed to delete stock item');
      console.error('Error deleting stock:', error);
    }
  };

  const isLowStock = (item) => {
    return item.reorder_point && item.quantity <= item.reorder_point;
  };

  const stats = {
    total: stocks.length,
    lowStock: stocks.filter(isLowStock).length,
    categories: new Set(stocks.map(s => s.category)).size
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10">
          <Package className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Stocks & Inventory</h1>
          <p className="text-muted-foreground mt-1">Manage your inventory and stock levels</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-sm text-muted-foreground">Total Items</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">{stats.lowStock}</div>
            <p className="text-sm text-muted-foreground">Low Stock Items</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.categories}</div>
            <p className="text-sm text-muted-foreground">Categories</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Inventory Items</CardTitle>
              <CardDescription>Track all your stock items and quantities</CardDescription>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stock Item
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Stock Item</DialogTitle>
                  <DialogDescription>Enter the details of the new stock item</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
                  <div className="grid gap-2">
                    <Label htmlFor="item_name">Item Name *</Label>
                    <Input
                      id="item_name"
                      value={newStock.item_name}
                      onChange={(e) => setNewStock({ ...newStock, item_name: e.target.value })}
                      placeholder="e.g., 22K Gold"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={newStock.category}
                        onValueChange={(value) => setNewStock({ ...newStock, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Gold">Gold</SelectItem>
                          <SelectItem value="Silver">Silver</SelectItem>
                          <SelectItem value="Diamond">Diamond</SelectItem>
                          <SelectItem value="Gemstones">Gemstones</SelectItem>
                          <SelectItem value="Accessories">Accessories</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="unit">Unit</Label>
                      <Select
                        value={newStock.unit}
                        onValueChange={(value) => setNewStock({ ...newStock, unit: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="grams">Grams</SelectItem>
                          <SelectItem value="kg">Kilograms</SelectItem>
                          <SelectItem value="pieces">Pieces</SelectItem>
                          <SelectItem value="carats">Carats</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="quantity">Quantity *</Label>
                      <Input
                        id="quantity"
                        type="number"
                        step="0.01"
                        value={newStock.quantity}
                        onChange={(e) => setNewStock({ ...newStock, quantity: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="reorder_point">Reorder Point</Label>
                      <Input
                        id="reorder_point"
                        type="number"
                        step="0.01"
                        value={newStock.reorder_point}
                        onChange={(e) => setNewStock({ ...newStock, reorder_point: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="supplier">Supplier</Label>
                    <Input
                      id="supplier"
                      value={newStock.supplier}
                      onChange={(e) => setNewStock({ ...newStock, supplier: e.target.value })}
                      placeholder="Supplier name"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={newStock.location}
                      onChange={(e) => setNewStock({ ...newStock, location: e.target.value })}
                      placeholder="Storage location"
                    />
                  </div>
                  <div className="border-t pt-4 mt-2">
                    <div className="flex items-center gap-2 mb-3">
                      <Bell className="h-4 w-4 text-primary" />
                      <Label className="text-base font-semibold">Set Reminder (Optional)</Label>
                    </div>
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="reminder_date">Reminder Date</Label>
                        <Input
                          id="reminder_date"
                          type="date"
                          value={newStock.reminder_date}
                          onChange={(e) => setNewStock({ ...newStock, reminder_date: e.target.value })}
                        />
                        <p className="text-xs text-muted-foreground">
                          Set a date to be reminded about this indent/order
                        </p>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="reminder_notes">Reminder Notes</Label>
                        <Textarea
                          id="reminder_notes"
                          value={newStock.reminder_notes}
                          onChange={(e) => setNewStock({ ...newStock, reminder_notes: e.target.value })}
                          placeholder="e.g., Place order with supplier, Check delivery status"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddStock}>Add Item</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {stocks.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No stock items found</p>
              <p className="text-muted-foreground">Add your first stock item to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Reorder Point</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stocks.map((stock) => (
                    <TableRow key={stock.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {stock.item_name}
                          {stock.reminder_date && (
                            <Bell className="h-3 w-3 text-primary" title={`Reminder: ${stock.reminder_date}`} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{stock.category}</TableCell>
                      <TableCell>
                        {stock.quantity} {stock.unit}
                      </TableCell>
                      <TableCell>
                        {stock.reorder_point ? `${stock.reorder_point} ${stock.unit}` : '-'}
                      </TableCell>
                      <TableCell>{stock.supplier || '-'}</TableCell>
                      <TableCell>
                        {isLowStock(stock) ? (
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Low Stock
                          </Badge>
                        ) : (
                          <Badge variant="default">In Stock</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingStock(stock);
                              setIsEditDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteStock(stock.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Stock Item</DialogTitle>
            <DialogDescription>Modify the stock item details</DialogDescription>
          </DialogHeader>
          {editingStock && (
            <div className="grid gap-4 py-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
              <div className="grid gap-2">
                <Label>Item Name *</Label>
                <Input
                  value={editingStock.item_name}
                  onChange={(e) => setEditingStock({ ...editingStock, item_name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Category</Label>
                  <Select
                    value={editingStock.category}
                    onValueChange={(value) => setEditingStock({ ...editingStock, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Gold">Gold</SelectItem>
                      <SelectItem value="Silver">Silver</SelectItem>
                      <SelectItem value="Diamond">Diamond</SelectItem>
                      <SelectItem value="Gemstones">Gemstones</SelectItem>
                      <SelectItem value="Accessories">Accessories</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Unit</Label>
                  <Select
                    value={editingStock.unit}
                    onValueChange={(value) => setEditingStock({ ...editingStock, unit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grams">Grams</SelectItem>
                      <SelectItem value="kg">Kilograms</SelectItem>
                      <SelectItem value="pieces">Pieces</SelectItem>
                      <SelectItem value="carats">Carats</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingStock.quantity}
                    onChange={(e) => setEditingStock({ ...editingStock, quantity: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Reorder Point</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingStock.reorder_point || ''}
                    onChange={(e) => setEditingStock({ ...editingStock, reorder_point: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Supplier</Label>
                <Input
                  value={editingStock.supplier || ''}
                  onChange={(e) => setEditingStock({ ...editingStock, supplier: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Location</Label>
                <Input
                  value={editingStock.location || ''}
                  onChange={(e) => setEditingStock({ ...editingStock, location: e.target.value })}
                />
              </div>
              <div className="border-t pt-4 mt-2">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="h-4 w-4 text-primary" />
                  <Label className="text-base font-semibold">Set Reminder (Optional)</Label>
                </div>
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label>Reminder Date</Label>
                    <Input
                      type="date"
                      value={editingStock.reminder_date || ''}
                      onChange={(e) => setEditingStock({ ...editingStock, reminder_date: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Set a date to be reminded about this indent/order
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <Label>Reminder Notes</Label>
                    <Textarea
                      value={editingStock.reminder_notes || ''}
                      onChange={(e) => setEditingStock({ ...editingStock, reminder_notes: e.target.value })}
                      placeholder="e.g., Place order with supplier, Check delivery status"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditStock}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StocksIndent;